import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, Loader2, X } from 'lucide-react';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  h1 {
    font-size: 2rem;
  }
`;

const Button = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.3s;
  
  &:hover {
    opacity: 0.9;
  }
  
  &.danger {
    background: #ef4444;
  }
  
  &.secondary {
    background: transparent;
    border: 1px solid var(--glass-border);
    color: var(--text-main);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-card);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--glass-border);
  
  th, td {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid var(--glass-border);
  }
  
  th {
    background: rgba(99, 102, 241, 0.05);
    font-weight: 600;
    color: var(--text-muted);
  }
  
  .actions {
    display: flex;
    gap: 10px;
  }
  
  button.icon-btn {
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    transition: color 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: 6px;
    
    &:hover {
      background: var(--input-bg);
      color: var(--text-main);
    }
    
    &.delete:hover {
      color: #ef4444;
      background: rgba(239, 68, 68, 0.1);
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  width: 95%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 20px;
  padding: 40px;
  border: 1px solid var(--glass-border);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--glass-border);
    border-radius: 10px;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--glass-border);
    
    h2 { 
      font-size: 1.8rem;
      font-weight: 700;
      background: linear-gradient(to right, var(--primary), var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    
    button {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid var(--glass-border);
      color: var(--text-muted);
      cursor: pointer;
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
      
      &:hover {
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
        border-color: rgba(239, 68, 68, 0.2);
      }
    }
  }

  .section-title {
    font-size: 0.9rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--primary);
    margin: 25px 0 15px;
    display: flex;
    align-items: center;
    gap: 10px;
    
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: linear-gradient(to right, rgba(99, 102, 241, 0.2), transparent);
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    label {
      font-weight: 500;
      color: var(--text-muted);
    }
    
    input, textarea, select {
      padding: 12px 16px;
      background: var(--input-bg);
      border: 1px solid var(--glass-border);
      border-radius: 8px;
      color: var(--text-main);
      font-family: inherit;
      font-size: 1rem;
      
      &:focus {
        outline: none;
        border-color: var(--primary);
      }
    }
    
    textarea {
      min-height: 120px;
      resize: vertical;
    }
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 10px;
  }
`;

const ManageOwnedProducts = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentProject, setCurrentProject] = useState({ title: '', description: '', category: 'Product', slug: '', img: '', client: '', year: '', status: 'active', sort_order: 0, is_owned: true, external_link: '' });
    const [imageFile, setImageFile] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const fetchProjects = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/projects`);
            const data = await response.json();
            const ownedProjects = Array.isArray(data) ? data.filter(p => p.is_owned) : [];
            setProjects(ownedProjects);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const handleOpenModal = (project = null) => {
        if (project) {
            setCurrentProject(project);
            setIsEditing(true);
        } else {
            setCurrentProject({ title: '', description: '', category: 'Product', slug: '', img: '', client: '', year: '', status: 'active', sort_order: 0, is_owned: true, external_link: '' });
            setIsEditing(false);
        }
        setImageFile(null);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setCurrentProject({ title: '', description: '', category: 'Product', slug: '', img: '', client: '', year: '', status: 'active', sort_order: 0, is_owned: true, external_link: '' });
        setImageFile(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('adminToken');
        const url = isEditing
            ? `${process.env.REACT_APP_API_URL || ''}/api/projects/${currentProject.id}`
            : `${process.env.REACT_APP_API_URL || ''}/api/projects`;
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const formData = new FormData();
            Object.keys(currentProject).forEach(key => {
                if (currentProject[key] !== null && key !== 'is_owned') {
                    formData.append(key, currentProject[key]);
                }
            });
            // Force the is_owned flag to true for Ecosystem projects ALWAYS
            formData.append('is_owned', 'true');
            if (imageFile) {
                formData.append('image', imageFile);
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                fetchProjects();
                handleCloseModal();
            } else {
                alert('Failed to save product');
            }
        } catch (err) {
            console.error(err);
            alert('Error connecting to server');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;

        const token = localStorage.getItem('adminToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/projects/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                fetchProjects();
            } else {
                alert('Failed to delete product');
            }
        } catch (err) {
            console.error(err);
            alert('Error connecting to server');
        }
    };

    return (
        <div>
            <PageHeader>
                <h1>Manage Ecosystem</h1>
                <Button onClick={() => handleOpenModal()}><Plus size={20} /> Add to Ecosystem</Button>
            </PageHeader>

            {loading ? (
                <div><Loader2 className="spin" /> Loading...</div>
            ) : (
                <Table>
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Sort Order</th>
                            <th>Status</th>
                            <th>Date Added</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {projects.map(p => (
                            <tr key={p.id}>
                                <td>{p.title}</td>
                                <td>{p.category}</td>
                                <td>{p.sort_order}</td>
                                <td>
                                    <span style={{
                                        padding: '4px 8px', borderRadius: '12px', fontSize: '0.8rem',
                                        background: p.status === 'active' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                        color: p.status === 'active' ? '#4ade80' : '#f87171'
                                    }}>
                                        {p.status}
                                    </span>
                                </td>
                                <td>{new Date(p.created_at).toLocaleDateString()}</td>
                                <td className="actions">
                                    <button className="icon-btn" onClick={() => handleOpenModal(p)}><Edit2 size={18} /></button>
                                    <button className="icon-btn delete" onClick={() => handleDelete(p.id)}><Trash2 size={18} /></button>
                                </td>
                            </tr>
                        ))}
                        {projects.length === 0 && (
                            <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No ecosystem projects found</td></tr>
                        )}
                    </tbody>
                </Table>
            )}

            {modalOpen && (
                <ModalOverlay onClick={handleCloseModal}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isEditing ? 'Edit Ecosystem Project' : 'Add Ecosystem Project'}</h2>
                            <button onClick={handleCloseModal}><X size={24} /></button>
                        </div>

                        <Form onSubmit={handleSubmit}>
                            <div className="section-title">Project Information</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px' }}>
                                <div className="form-group">
                                    <label>Project Name</label>
                                    <input
                                        type="text"
                                        value={currentProject.title}
                                        onChange={e => setCurrentProject({ ...currentProject, title: e.target.value })}
                                        placeholder="Enter product name..."
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>URL Slug</label>
                                    <input
                                        type="text"
                                        value={currentProject.slug || ''}
                                        onChange={e => setCurrentProject({ ...currentProject, slug: e.target.value })}
                                        placeholder="Auto-generated if blank"
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                                <div className="form-group">
                                    <label>Category</label>
                                    <input
                                        type="text"
                                        value={currentProject.category || 'Product'}
                                        onChange={e => setCurrentProject({ ...currentProject, category: e.target.value })}
                                        placeholder="e.g. SaaS Platform"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>External Link (No-index)</label>
                                    <input
                                        type="text"
                                        value={currentProject.external_link || ''}
                                        onChange={e => setCurrentProject({ ...currentProject, external_link: e.target.value })}
                                        placeholder="https://yourapp.com"
                                    />
                                </div>
                            </div>

                            <div className="section-title">Media & Visuals</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'end' }}>
                                <div className="form-group">
                                    <label>Upload New Image</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => setImageFile(e.target.files[0])}
                                        style={{ padding: '8px' }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Or Image URL</label>
                                    <input
                                        type="text"
                                        value={currentProject.img || ''}
                                        onChange={e => setCurrentProject({ ...currentProject, img: e.target.value })}
                                        placeholder="https://..."
                                    />
                                </div>
                            </div>

                            <div className="section-title">Project Details</div>
                            <div className="form-group">
                                <label>Description (Markdown supported)</label>
                                <textarea
                                    value={currentProject.description}
                                    onChange={e => setCurrentProject({ ...currentProject, description: e.target.value })}
                                    placeholder="Write a detailed description of the product..."
                                    required
                                    style={{ minHeight: '180px' }}
                                />
                            </div>

                            <div className="section-title">Status & Order</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={currentProject.status}
                                        onChange={e => setCurrentProject({ ...currentProject, status: e.target.value })}
                                    >
                                        <option value="active">Active</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Sort Order</label>
                                    <input
                                        type="number"
                                        value={currentProject.sort_order}
                                        onChange={e => setCurrentProject({ ...currentProject, sort_order: parseInt(e.target.value) || 0 })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Year</label>
                                    <input
                                        type="text"
                                        value={currentProject.year || ''}
                                        onChange={e => setCurrentProject({ ...currentProject, year: e.target.value })}
                                        placeholder="e.g. 2026"
                                    />
                                </div>
                            </div>

                            <div className="form-actions" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid var(--glass-border)' }}>
                                <Button type="button" className="secondary" onClick={handleCloseModal} style={{ padding: '12px 25px' }}>Cancel</Button>
                                <Button type="submit" style={{ padding: '12px 35px' }}>
                                    {isEditing ? 'Save Ecosystem Changes' : 'Create Ecosystem Project'}
                                </Button>
                            </div>
                        </Form>
                    </ModalContent>
                </ModalOverlay>
            )}
            <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ManageOwnedProducts;
